/**
 * Utility functions for managing filter URL parameters
 * Handles the conversion between filter state and URL query parameters
 */

export interface FilterUrlState {
  brands: number[];
  skinTypes: number[];
  skinConcerns: number[];
  ingredients: number[];
  minPrice?: number;
  maxPrice?: number;
  sortBy?: string;
  page?: number;
}

/**
 * Build filter URL string from current filter state
 * Format: price[min_max]|brand[termID]|skin_type[termID+termID]|skin_concern[termID+termID]|ingredient[termID+termID]|sort[value]|page[number]
 */
export const buildFilterUrlString = (state: FilterUrlState): string => {
  const parts: string[] = [];

  // Add price filter
  if (state.minPrice !== undefined || state.maxPrice !== undefined) {
    const min = state.minPrice || 0;
    const max = state.maxPrice || 0;
    parts.push(`price[${min}_${max}]`);
  }

  // Add brand filter (single selection)
  if (state.brands.length > 0) {
    parts.push(`brand[${state.brands[0]}]`);
  }

  // Add explicit taxonomy filters
  if (state.skinTypes.length > 0) {
    const tagString = state.skinTypes.join('+');
    parts.push(`skin_type[${tagString}]`);
  }

  if (state.skinConcerns.length > 0) {
    const tagString = state.skinConcerns.join('+');
    parts.push(`skin_concern[${tagString}]`);
  }

  if (state.ingredients.length > 0) {
    const tagString = state.ingredients.join('+');
    parts.push(`ingredient[${tagString}]`);
  }

  // Add sort filter (only if not default)
  if (state.sortBy && state.sortBy !== 'BestSeller') {
    parts.push(`sort[${state.sortBy}]`);
  }

  // Add page filter (only if not page 1)
  if (state.page && state.page > 1) {
    parts.push(`page[${state.page}]`);
  }

  return parts.join('|');
};

/**
 * Parse filter URL string to filter state
 * Format: price[min_max]|brand[termID]|skin_type[termID+termID]|skin_concern[termID+termID]|ingredient[termID+termID]|sort[value]|page[number]
 */
export const parseFilterUrlString = (filterString: string): FilterUrlState => {
  const state: FilterUrlState = {
    brands: [],
    skinTypes: [],
    skinConcerns: [],
    ingredients: [],
  };

  if (!filterString) {
    return state;
  }

  const parts = filterString.split('|');

  for (const part of parts) {
    // Parse price filter
    const priceMatch = part.match(/^price\[(\d+)_(\d+)\]$/);
    if (priceMatch) {
      const min = parseInt(priceMatch[1], 10);
      const max = parseInt(priceMatch[2], 10);
      state.minPrice = min > 0 ? min : undefined;
      state.maxPrice = max > 0 ? max : undefined;
      continue;
    }

    // Parse brand filter
    const brandMatch = part.match(/^brand\[(\d+)\]$/);
    if (brandMatch) {
      state.brands = [parseInt(brandMatch[1], 10)];
      continue;
    }

    // Parse skin type filter
    const skinTypeMatch = part.match(/^skin_type\[([0-9+\s]+)\]$/);
    if (skinTypeMatch) {
      state.skinTypes = skinTypeMatch[1]
        .split(/[+\s]+/) // Split by both + and spaces
        .map((id) => parseInt(id.trim(), 10))
        .filter((id) => !isNaN(id) && id > 0); // Filter out invalid numbers
      continue;
    }

    // Parse skin concern filter
    const skinConcernMatch = part.match(/^skin_concern\[([0-9+\s]+)\]$/);
    if (skinConcernMatch) {
      state.skinConcerns = skinConcernMatch[1]
        .split(/[+\s]+/) // Split by both + and spaces
        .map((id) => parseInt(id.trim(), 10))
        .filter((id) => !isNaN(id) && id > 0); // Filter out invalid numbers
      continue;
    }

    // Parse ingredient filter
    const ingredientMatch = part.match(/^ingredient\[([0-9+\s]+)\]$/);
    if (ingredientMatch) {
      state.ingredients = ingredientMatch[1]
        .split(/[+\s]+/) // Split by both + and spaces
        .map((id) => parseInt(id.trim(), 10))
        .filter((id) => !isNaN(id) && id > 0); // Filter out invalid numbers
      continue;
    }

    // Parse sort filter
    const sortMatch = part.match(/^sort\[([a-zA-Z0-9]+)\]$/);
    if (sortMatch) {
      state.sortBy = sortMatch[1];
      continue;
    }

    // Parse page filter
    const pageMatch = part.match(/^page\[(\d+)\]$/);
    if (pageMatch) {
      const pageNumber = parseInt(pageMatch[1], 10);
      state.page = pageNumber > 0 ? pageNumber : undefined;
      continue;
    }
  }

  return state;
};

/**
 * Update URL with filter parameters without page refresh
 */
export const updateFilterUrl = (filterString: string, _router?: unknown, _pathname?: string) => {
  const url = new URL(window.location.href);

  // Remove existing filters parameter
  url.searchParams.delete('filters');

  // Build URL manually to avoid encoding special characters
  let newUrl = url.origin + url.pathname;

  // Add other existing query parameters first
  const otherParams = url.searchParams.toString();
  if (otherParams) {
    newUrl += `?${otherParams}`;
  }

  // Add filters parameter without encoding to keep + signs readable
  if (filterString) {
    const separator = otherParams ? '&' : '?';
    newUrl += `${separator}filters=${filterString}`;
  }

  // Use replace to avoid adding to browser history for every filter change
  window.history.replaceState(null, '', newUrl);
};
